import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ACTIONS from "../../../../redux/action.js";
import axios from "../../../../shared/axios/axiosProxy.js";
import { legalDocuments } from "../../../../shared/data/legalDocuments.js";
import i18n from "../../../../utils/i18n.js";
import AspContactPriceBand from "../aspContactPriceBand/AspContactPriceBand.jsx";
import ContactOfferIntro from "../contactOfferIntro/ContactOfferIntro.jsx";
import ContactInfo from "../contactInfo/contactInfo.jsx";
import CouponCode from "../couponCode/CouponCode.jsx";
import SearchDataPreview from "../searchDataPreview/searchDataPreview.jsx";
import ContactExpertCta from "./ContactExpertCta.jsx";
import ContactSelectionChips from "./ContactSelectionChips.jsx";
import ContactToggleSection from "./ContactToggleSection.jsx";
import classes from "./contactPage.module.scss";

// Dev/monitoring panel
const HIDE_SEARCH_DATA_PREVIEW = true;

// Mirrors legacy aspProviders.jsx `numberRoundOutput`/`numberWithCommas` — kept
// in sync since ProviderGradeSelection (the intended owner of this calc) is not
// mounted on this step and never runs.
const numberRoundOutput = (data) => Math.round(Number(data || 0) * 0.01) * 100;

const numberWithCommas = (x) => {
  if (x === null || x === undefined || x === "") return "";
  const n = typeof x === "number" ? x : parseInt(String(x).replace(/\D/g, ""), 10);
  if (Number.isNaN(n)) return "";
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Mirrors the `isDiscounted` derivation in AspContactPriceBand/ProviderGradeSelection.
const deriveIsDiscounted = (companyStore) => {
  const d = companyStore.discount;
  if (!d) return false;
  if (d.companyType === "any") return true;
  return (
    typeof d.companyType === "string" && d.companyType.includes(companyStore.companytype)
  );
};

const ContactPage = ({
  createAspRequest,
  fetchAspTopProvidersByMatchScore,
  handleNext,
  match_score_weights,
  onRequestError,
}) => {
  const dispatch = useDispatch();
  const companyStore = useSelector((state) => state.company_store);

  const isNameValid = useMemo(() => {
    const full = (companyStore.companyctcname || "").trim();
    const i = full.indexOf(" ");
    return (
      i > 0 &&
      full.slice(0, i).trim().length > 0 &&
      full.slice(i + 1).trim().length > 0
    );
  }, [companyStore.companyctcname]);
  const isEmailValid = useMemo(() => {
    const email = (companyStore.email || "").trim();
    if (!email) return false;
    const validEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    return validEmailRegex.test(email);
  }, [companyStore.email]);
  const isPhoneValid = useMemo(
    () => (companyStore.companyPhone || "").trim().length >= 8,
    [companyStore.companyPhone],
  );
  const isFormValid = isNameValid && isEmailValid && isPhoneValid;

  const [touchedFields, setTouchedFields] = useState({
    name: false,
    email: false,
    phone: false,
  });
  const showNameError = touchedFields.name && !isNameValid;
  const showEmailError = touchedFields.email && !isEmailValid;
  const showPhoneError = touchedFields.phone && !isPhoneValid;

  const handleNameBlur = useCallback(() => {
    setTouchedFields((prev) => (prev.name ? prev : { ...prev, name: true }));
  }, []);

  const handleEmailBlur = useCallback(() => {
    setTouchedFields((prev) => (prev.email ? prev : { ...prev, email: true }));
  }, []);

  const handlePhoneBlur = useCallback(() => {
    setTouchedFields((prev) => (prev.phone ? prev : { ...prev, phone: true }));
  }, []);

  const [reqDisabled, setReqDisabled] = useState(false);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [messageOpen, setMessageOpen] = useState(() =>
    Boolean((companyStore.reqMessage || "").trim()),
  );
  const [couponOpen, setCouponOpen] = useState(false);
  const showOfferIntro = Boolean(companyStore.accounting) && !contactRevealed;

  useEffect(() => {
    if (companyStore.discount) {
      setCouponOpen(true);
    }
  }, [companyStore.discount]);

  const handleAspRequest = useCallback(async () => {
    if (!isFormValid || typeof createAspRequest !== "function") return;

    const trimmedName = (companyStore.companyctcname || "").trim();
    const trimmedEmail = (companyStore.email || "").trim();
    const trimmedPhone = (companyStore.companyPhone || "").trim();

    dispatch(
      ACTIONS.addCompanyInfo({
        id: "gdpr",
        data: true,
        stateObject: "company_store",
      }),
    );

    const contactFieldUpdates = [
      { id: "companyctcname", data: trimmedName, current: companyStore.companyctcname },
      { id: "email", data: trimmedEmail, current: companyStore.email },
      { id: "companyPhone", data: trimmedPhone, current: companyStore.companyPhone },
    ];
    contactFieldUpdates.forEach(({ id, data, current }) => {
      if (current !== data) {
        dispatch(
          ACTIONS.addCompanyInfo({
            id,
            data,
            stateObject: "company_store",
          }),
        );
      }
    });

    setReqDisabled(true);
    try {
      // proposedSinglePrice/originalSinglePrice/isCommission/isDiscounted are normally
      // populated by ProviderGradeSelection, but that component isn't mounted on this
      // step (this flow has no provider-grade choice), so they're computed here instead.
      const singleSource = companyStore.potential_providers?.[0]?.[0]?.singleEndPrice ?? 0;
      const formattedSinglePrice = numberWithCommas(numberRoundOutput(singleSource));

      const response = await createAspRequest({
        ...companyStore,
        companyctcname: trimmedName,
        email: trimmedEmail,
        companyPhone: trimmedPhone,
        gdpr: true,
        proposedSinglePrice: formattedSinglePrice,
        proposedContPrice: companyStore.proposedContPrice,
        isCommission: true,
        individual: false,
        isDiscounted: deriveIsDiscounted(companyStore),
        originalSinglePrice: formattedSinglePrice,
        originalContPrice: companyStore.originalContPrice,
      });

      const reqId = response?.data?.result?._id;
      if (!reqId) {
        console.error(
          "ASP request finished without reqID in response; not advancing step.",
          response?.data,
        );
        setReqDisabled(false);
        onRequestError?.();
        return;
      }

      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "sf_step_asp_request_sent",
          event_category: "aspflow",
        });
      }

      handleNext?.();
    } catch (error) {
      console.error("Error during the creation of the ASP request:", error);
      setReqDisabled(false);
      onRequestError?.();
    }
  }, [companyStore, createAspRequest, dispatch, handleNext, isFormValid, onRequestError]);

  const handleSubmitClick = useCallback(() => {
    if (reqDisabled) {
      return;
    }
    if (!isFormValid) {
      setTouchedFields({ name: true, email: true, phone: true });
      return;
    }
    handleAspRequest();
  }, [handleAspRequest, isFormValid, reqDisabled]);

  const handleReqMessageChange = useCallback(
    (event) => {
      dispatch(
        ACTIONS.addCompanyInfo({
          id: "reqMessage",
          data: event.target.value,
          stateObject: "company_store",
        }),
      );
    },
    [dispatch],
  );

  const handleReqMessageBlur = useCallback(() => {
    if (!companyStore.searchID) return;

    axios
      .patch(
        `/api/search/${companyStore.searchID}`,
        { reqMessage: companyStore.reqMessage || "" },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
      .catch((error) => {
        console.error(
          "Error saving reqMessage to searchSchema:",
          error.response?.data || error.message,
        );
      });
  }, [companyStore.reqMessage, companyStore.searchID]);

  const newsletterLabel = (
    <span>{i18n.t("searchflow.newslettercontact")}</span>
  );

  if (showOfferIntro) {
    return (
      <ContactOfferIntro
        fetchAspTopProvidersByMatchScore={fetchAspTopProvidersByMatchScore}
        match_score_weights={match_score_weights}
        onReveal={() => setContactRevealed(true)}
      />
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <div className={classes.summaryBlock}>
              <AspContactPriceBand
                fetchAspTopProvidersByMatchScore={fetchAspTopProvidersByMatchScore}
                match_score_weights={match_score_weights}
                staticDisplay={Boolean(companyStore.accounting)}
                variant="contactPage"
              />
              {!companyStore.accounting ? <ContactSelectionChips /> : null}
            </div>

            <div className={classes.formPanel}>
              <ContactExpertCta />

              <div className={classes.formSection}>
                <ContactInfo
                  splitNameFields
                  layout="contactGrid"
                  className={classes.contactFields}
                  lastNameLabel={i18n.t("searchflow.lastnamelabel")}
                  firstNameLabel={i18n.t("searchflow.yourgivenname")}
                  emailLabel={i18n.t("searchflow.emaillabelcontact")}
                  phoneLabel={i18n.t("searchflow.phonelabelcontact")}
                  nameError={showNameError}
                  emailError={showEmailError}
                  phoneError={showPhoneError}
                  onNameBlur={handleNameBlur}
                  onEmailBlur={handleEmailBlur}
                  onPhoneBlur={handlePhoneBlur}
                  middleSlot={
                    <div className={`${classes.newsletterRow} newsletterRow`}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="marketing"
                            name="marketing"
                            checked={Boolean(companyStore.marketing)}
                            onChange={(event) => {
                              dispatch(
                                ACTIONS.addCompanyInfo({
                                  id: event.target.id,
                                  data: event.target.checked,
                                  stateObject: "company_store",
                                }),
                              );
                            }}
                            color="primary"
                          />
                        }
                        label={newsletterLabel}
                      />
                    </div>
                  }
                />

                <div className={classes.optionalPanel}>
                  <ContactToggleSection
                    variant="embedded"
                    label={i18n.t("searchflow.moredetailstoggle")}
                    checked={messageOpen}
                    onChange={setMessageOpen}
                  >
                    <TextField
                      className={classes.messageField}
                      multiline
                      minRows={4}
                      variant="outlined"
                      label={i18n.t("searchflow.messagelabel")}
                      InputLabelProps={{ shrink: true }}
                      placeholder=""
                      value={companyStore.reqMessage || ""}
                      onChange={handleReqMessageChange}
                      onBlur={handleReqMessageBlur}
                      fullWidth
                    />
                  </ContactToggleSection>

                  {companyStore.accounting ? (
                    <>
                      <div className={classes.optionalDivider} role="separator" />
                      <ContactToggleSection
                        variant="embedded"
                        label={i18n.t("searchflow.hascoupontoggle")}
                        checked={couponOpen}
                        onChange={setCouponOpen}
                      >
                        <CouponCode variant="contactPage" />
                      </ContactToggleSection>
                    </>
                  ) : null}
                </div>
              </div>

              <div className={classes.submitArea}>
                <p className={classes.legalText}>
                  {i18n.t("searchflow.legalprefix")}{" "}
                  <a
                    className={classes.legalLink}
                    rel="noopener noreferrer"
                    target="_blank"
                    href={legalDocuments.gdpr}
                  >
                    {i18n.t("gdpr.part2")}
                  </a>{" "}
                  {i18n.t("gdpr.part3")}{" "}
                  <a
                    className={classes.legalLink}
                    rel="noopener noreferrer"
                    target="_blank"
                    href={legalDocuments.aszf}
                  >
                    {i18n.t("gdpr.part4")}
                  </a>
                  {i18n.t("gdpr.part5")}
                </p>
                <button
                  type="button"
                  className={classes.submitButton}
                  onClick={handleSubmitClick}
                  aria-disabled={!isFormValid || reqDisabled}
                >
                  <span>
                    {reqDisabled
                      ? i18n.t("searchflow.submitting")
                      : i18n.t("searchflow.requestoffercta")}
                  </span>
                  <ChevronRightIcon className={classes.submitIcon} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {!HIDE_SEARCH_DATA_PREVIEW && <SearchDataPreview />}
      </div>
    </div>
  );
};

export default ContactPage;
